import { createContext, ReactNode, useContext, useEffect } from 'react';
import { PartnerConfig } from 'src/shared/types';
import { usePartnerConfig } from 'src/shared/hooks/usePartnerConfig';
import { usePartnerConfigStore } from 'src/shared/stores/usePartnerConfigStore';
import { applyPartnerTheme } from 'src/shared/utils/applyPartnerTheme';

type PartnerConfigContextProviderProps = {
  children: ReactNode;
};

type PartnerConfigContextProps = {
  partnerConfig: PartnerConfig | null;
  isLoading: boolean;
  isFallback: boolean;
  refetch: () => void;
};

export const PartnerConfigContext = createContext<PartnerConfigContextProps>(
  {} as PartnerConfigContextProps,
);

export function usePartnerConfigContext() {
  return useContext(PartnerConfigContext);
}

export function PartnerConfigProvider({
  children,
}: PartnerConfigContextProviderProps) {
  const { partnerConfig, isLoading, isFallback, refetch } = usePartnerConfig();
  const { setPartnerConfig, setIsLoading } = usePartnerConfigStore();

  useEffect(() => {
    if (isLoading && !partnerConfig) {
      setPartnerConfig(null);
      return;
    }

    setPartnerConfig(partnerConfig);
  }, [isLoading, partnerConfig, setPartnerConfig]);

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  useEffect(() => {
    if (isLoading && !partnerConfig) {
      applyPartnerTheme(null);
      return;
    }

    applyPartnerTheme(partnerConfig);
  }, [isLoading, partnerConfig]);

  return (
    <PartnerConfigContext.Provider
      value={{ partnerConfig, isLoading, isFallback, refetch }}
    >
      {children}
    </PartnerConfigContext.Provider>
  );
}
